import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AuthShell, FeatIcon, SvgArrow,
} from "./AuthShell.js";
import { useAuth } from "../../lib/auth-context.js";

/**
 * /check-email — confirmation + resend.
 *
 * Reached from /sign-up (?from=signup) or /forgot (?from=reset).
 * Both flows ship a 6-digit code; the only difference is where the
 * verify step routes afterwards. The "I have the code" button takes
 * the user to /verify with the appropriate purpose flag.
 */
export const CheckEmail = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { requestPasswordReset, requestSignIn } = useAuth();
  const fromSignup = params.get("from") === "signup";
  const fromReset  = params.get("from") === "reset";
  const email = sessionStorage.getItem("wo.auth.email") || "";

  const [secs, setSecs] = useState(42);
  const [resending, setResending] = useState(false);
  useEffect(() => {
    if (!email) navigate("/sign-in", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (secs <= 0) return;
    const t = setTimeout(() => setSecs((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secs]);

  const onResend = async () => {
    if (secs > 0 || resending || !email) return;
    setResending(true);
    const r = fromReset
      ? await requestPasswordReset(email)
      : await requestSignIn(email);
    setResending(false);
    if (r.ok) {
      if (r.devOtp) sessionStorage.setItem("wo.auth.devOtp", r.devOtp);
      setSecs(42);
    }
  };

  const toVerify = () => {
    if (fromReset) navigate("/verify?reset=1");
    else if (fromSignup) navigate("/verify?new=1");
    else navigate("/verify");
  };

  return (
    <AuthShell
      brand={{
        eyebrow: "A short wait",
        quote: <>Take a slow breath while it lands. <em>Most things</em> arrive faster than we expect.</>,
        cite: "— While you wait",
        features: [
          { icon: FeatIcon.mail, label: "Usually arrives within 30 seconds" },
        ],
      }}
      topRight={<Link to="/sign-in" className="btn-ghost">← Back</Link>}
      footer={<>Wrong email? <Link to="/forgot">Try again →</Link></>}
    >
      <div style={{ textAlign: "center", width: "100%" }}>
        <div className="pulse">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
            <path d="M4 6h16v12H4V6z" stroke="rgb(var(--wo-mustard))" strokeWidth="1.8" strokeLinejoin="round" />
            <path d="M4 8l8 6 8-6" stroke="rgb(var(--wo-mustard))" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        <div className="eyebrow mustard">Sent</div>
        <h1 className="wo-title" style={{ marginTop: 6 }}>Check your inbox.</h1>
        <p className="wo-sub">
          We sent a 6-digit code to <strong>{email}</strong>. It expires in 10 minutes.
          {fromReset ? " Enter it on the next screen to set a new password." : ""}
        </p>

        <div style={{
          background: "rgb(var(--wo-cream-2))", borderRadius: 14, padding: "16px 18px",
          textAlign: "left", margin: "8px 0 22px",
        }}>
          <div className="eyebrow" style={{ color: "rgb(var(--wo-mustard))" }}>A small tip</div>
          <p style={{ margin: "6px 0 0", fontSize: 13.5, lineHeight: 1.5, color: "rgb(var(--wo-ink-soft))" }}>
            If you don&apos;t see it within a minute, check your spam folder — or try a different email. It happens.
          </p>
        </div>

        <button type="button" className="btn-primary" onClick={toVerify}>
          I have the code
          {SvgArrow}
        </button>

        <button
          type="button"
          className="btn-ghost"
          style={{ width: "100%", marginTop: 14, height: 44 }}
          onClick={onResend}
          disabled={secs > 0 || resending}
        >
          {resending ? "Resending…" : secs > 0 ? `Resend in 0:${String(secs).padStart(2, "0")}` : "Resend email"}
        </button>

        <p className="reflect">&ldquo;Patience is a small form of kindness.&rdquo;</p>
      </div>
    </AuthShell>
  );
};
